/* ---------------------------------------------------------------------------
   "Import Bridge Model" — a CSiBridge export into a saved model, without ever
   showing the modeller.

   The action is on the bridge record and it stays on the bridge record: pick the
   export, and a moment later the record's own viewport is the imported bridge and
   the model in Cloudflare is the imported model. Nobody is sent into the
   workspace to make that happen — there is no longer anywhere to be sent.

   THE FIRST IMPORT CREATES THE MODEL IT LANDS IN. A structure with nothing
   modelled has nothing to import into, and the modeller's own new-bridge journey
   cannot run off screen: `?template=` opens a modal, and a modal in a frame nobody
   can see is a run that never finishes. So the register asks the API for an empty
   shell first (POST /api/modeler/models) and imports into that. All the modeller
   needs to open a model is an entry in its list; a state key that does not exist
   yet is already tolerated, and the import replaces what the defaults put there.

   IT STILL RUNS THE MODELLER, in a frame off the side of the page. That is not a
   shortcut, it is the only correct way to do it. Applying a CSiBridge export
   means the modeller's own parser, its alignment fallback, its substructure
   mapping and its component catalogs; saving the result means
   `SerializeModelState`, whose shape is vendored, unversioned and deliberately
   not read out here (see the README's note on why the register does not parse
   model geometry). Reimplementing either against a copy of somebody else's
   internals is how you get a register that writes models the modeller cannot
   open. So the modeller does the work — it is simply not on screen while it
   does, and it talks over the postMessage channel the flush and the component
   tree already use.

   Two things about the hidden frame are load-bearing:

   IT IS LAID OUT, not hidden. `display:none` or a zero box would give Canvas3D a
   0×0 canvas, and `CaptureThumbnail` returns null on one of those — so the record
   would get the imported model with the pre-import picture still on it. It is
   moved off screen at full size instead. See .mimp-stage in app.css.

   IT IS THE ONLY FRAME ON THAT MODEL. The record's live viewport is unmounted
   for the duration (src/modeler/viewport.ts keys off `modelImport`), because two
   framed modellers on one model are two writers of
   `bim_modeler_model_state_<id>`: the idle one's autosave would put the
   pre-import state back over the imported one, and last-write-wins means the
   loser is silent about it.

   WHY THE FILE IS CHOSEN OUT HERE. A browser opens a file chooser only during
   transient user activation — a few seconds after a real click, in the document
   that click happened in. What follows this click is a frame that has to fetch a
   1.6 MB chunk, install the storage shim and boot a model first. So the picker
   runs in the click handler, where the activation is, and only the file's text
   crosses into the frame.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import * as api from '../core/api.ts';
import { exportModelGeometry } from './geometry.ts';
import { dropGeometry } from '../core/geometryCache.ts';
import { NO_MESH_REASON, exportHasMesh } from '../data/cloudExport.ts';
import { hasLocalMesh, repairMeshStore } from './meshPresence.ts';
import { actions, store, type ModelImportResult, type State } from '../core/store.ts';

/**
 * What the picker accepts.
 *
 * The bundle and the alignment are both JSON, and which is which is decided by
 * the modeller from their CONTENT rather than their names — so this is one
 * filter and multi-select, matching the modeller's own picker exactly.
 */
const ACCEPT = '.json,application/json';

/**
 * How long each stage may take before the register stops waiting.
 *
 * Generous, because every one of them is "slow link plus a big model" rather
 * than "something is wrong", and the cost of giving up early is an import the
 * user has to do again. The state fetch inside the frame has its own 8 s
 * timeout and falls back to the local mirror, so BOOT covers that plus the
 * 1.6 MB chunk; APPLY is one synchronous pass over the export, which for an
 * eighteen-span flyover with a mesh is seconds; SAVE is the modeller's 500 ms
 * autosave debounce, its 4 s thumbnail debounce and the shim's 1.5 s flush,
 * plus the PUT itself.
 */
const BOOT_MS = 45_000;
const APPLY_MS = 60_000;
const SAVE_MS = 25_000;

/** Keys the modeller writes for a model, which the shim reports as it flushes them. */
const STATE_KEY = 'bim_modeler_model_state_';
const THUMB_KEY = 'bim_modeler_model_thumb_';

/* --------------------------- the picker --------------------------- */

/**
 * Ask for the export, then import it in the background.
 *
 * Called straight from the button's click handler, and it opens the picker
 * synchronously for the reason at the top of this file — everything else happens
 * after the user has chosen.
 *
 * Cancelling does nothing at all: no frame, no state change, no dialog. The
 * button says "Import", so doing anything on a cancelled picker would be
 * answering a question the user just declined to answer.
 */
export function importBridgeModel(
  bridgeId: string,
  /**
   * The model to import into, or null to have one made.
   *
   * Null is the ordinary case on a structure nobody has modelled yet, which since
   * building was removed is every structure until its first import.
   */
  model: { id: string; name: string } | null,
  /** What to call a model that has to be created. The structure's own name. */
  fallbackName: string,
): void {
  const input = h('input', {
    type: 'file',
    accept: ACCEPT,
    multiple: true,
    style: 'display:none',
  });

  input.addEventListener('cancel', () => input.remove());
  input.addEventListener('change', () => {
    const files = Array.from(input.files ?? []);
    input.remove();
    if (files.length === 0) return;

    /* ANNOUNCED BEFORE ANYTHING IS DONE, not after. Everything below this line
       takes time the user can notice — a two-thirds-of-a-megabyte export off
       disk, then a request to create a model to put it in — and until this call
       the record showed the idle button, so a click looked ignored for as long as
       that took. The record is now showing progress by the time the file picker
       has finished closing. */
    actions.beginModelImport(bridgeId, model);

    void (async () => {
      let texts: string[];
      try {
        texts = await Promise.all(files.map((f) => f.text()));
      } catch {
        // The file went away between the picker closing and this reading it.
        // Reported through the summary rather than an alert, because the record
        // is already showing an import in progress and something has to take it
        // back down.
        await actions.finishModelImport({
          bridgeId,
          modelId: model?.id ?? '',
          modelName: model?.name ?? fallbackName,
          ok: false,
          name: '',
          applied: [],
          warnings: [],
          error: 'That file could not be read. Please choose the export again.',
          saveUnconfirmed: false,
    geometryError: null,
        });
        return;
      }
      /* An export with no mesh would be rebuilt from its parameters, which is
         not a model this register keeps. Refused before a shell is minted. */
      if (!exportHasMesh(texts)) {
        await actions.finishModelImport({
          bridgeId,
          modelId: model?.id ?? '',
          modelName: model?.name ?? fallbackName,
          ok: false,
          name: '',
          applied: [],
          warnings: [],
          error: NO_MESH_REASON,
          saveUnconfirmed: false,
          geometryError: null,
        });
        return;
      }
      /* Created only once the file has been read. A shell minted for an import
         that then failed to even open its file would be an empty model on the
         record with nothing in it and no way to tell why. */
      let target = model;
      if (target === null) {
        actions.modelImportPhase('creating');
        try {
          target = await api.createModel(bridgeId, fallbackName);
          actions.modelImportTarget(target);
        } catch (err) {
          await actions.finishModelImport({
            bridgeId,
            modelId: '',
            modelName: fallbackName,
            ok: false,
            name: '',
            applied: [],
            warnings: [],
            error:
              err instanceof api.ApiError
                ? `A model could not be created to import into — ${err.message}`
                : 'A model could not be created to import into.',
            saveUnconfirmed: false,
    geometryError: null,
          });
          return;
        }
      }
      await runImport(bridgeId, target, texts);
    })();
  });

  document.body.appendChild(input);
  input.click();
}

/* --------------------------- the hidden run --------------------------- */

/** A message from the frame. Loosely typed — it is checked field by field below. */
interface FrameMessage {
  source?: string;
  type?: string;
  ok?: boolean;
  name?: string;
  applied?: unknown;
  warnings?: unknown;
  error?: string | null;
  message?: string;
  keys?: unknown;
}

/** Strings out of a message field that arrived across a boundary. */
function lines(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * The URL the hidden frame loads.
 *
 * `embed=viewport` because this is the modeller reduced to its canvas, which is
 * exactly what an import needs: no chrome to boot, and `mode=modeler` keeps the
 * 3D view the one on screen, which is what `PersistThumbnail` requires before it
 * will capture anything. `theme` only decides what the thumbnail's background
 * will look like, and matching the register's is the least surprising choice.
 */
function importSrc(s: Readonly<State>, bridgeId: string, modelId: string): string {
  return `modeler?${new URLSearchParams({
    bridge: bridgeId,
    model: modelId,
    mode: 'modeler',
    embed: 'viewport',
    theme: s.theme,
  })}`;
}

/* --------------------------- restoring on a new device --------------------------- */

/** Models a restore has already been attempted for, so it is tried once per load. */
const restoreTried = new Set<string>();

/**
 * Put an imported model's mesh on THIS device, from the export the server holds.
 *
 * WHAT IT IS FOR. An imported model's triangles live in the importing browser's
 * IndexedDB and nowhere else (see ./meshPresence.ts), while its state tells every
 * browser `hasImportedMesh: true`. So the model opened anywhere else — a colleague's
 * laptop, or the same laptop with the dev server on another port, since IndexedDB is
 * keyed by origin and a port is part of one — drew a bridge with no substructure and
 * said nothing about why. Now the export document is on the server as a matter of
 * course, and this is the other half: the device that lacks the mesh replays it.
 *
 * DOES NOTHING UNLESS ALL FOUR ARE TRUE, and each one is a case where acting would
 * be worse than not:
 *
 *   - the model displays an imported mesh (`imported === true`). A parametric model
 *     needs no mesh; it is rebuilt from its parameters wherever it is opened. Note
 *     this is not `!== false`: `null` means the server could not tell, and replaying
 *     an import over a model on a guess is not a thing to do.
 *   - the server has the source (`hasSource`). Every import since this landed stores
 *     one; models imported before it do not have one, and there is nothing to be
 *     done for those but import again. Saying so beats a request that 404s.
 *   - this device does not already have the mesh. The common case, and the check is
 *     a count of one key.
 *   - nothing else is importing. The frame this opens is a second writer of the
 *     model's state, which is the one thing src/modeler/importModel.ts's header
 *     exists to prevent.
 *
 * ONCE PER LOAD PER MODEL, whatever the outcome. A restore that failed will fail
 * again for the same reason, and the record re-reads its model index on every
 * navigation — without this it would replay on every one of them.
 *
 * QUIET ON FAILURE, deliberately, and this is the one judgement here worth
 * arguing with. Nobody asked for this: it is repair work the app noticed it needed.
 * A dialog for it would interrupt an engineer who came to read an inspection
 * report to tell them about a device-local cache they have never heard of. What
 * they see instead is the truth either way — the bridge, or the record saying the
 * model has no geometry on this device — and the console carries the reason.
 */
export async function restoreImportedModel(
  bridgeId: string,
  model: {
    id: string;
    name: string;
    imported: boolean | null;
    hasSource: boolean;
    /** Whether the model's own geometry is in the bucket — see the guard below. */
    hasGeometry?: boolean;
  },
): Promise<void> {
  /* NOTHING TO REPAIR WHEN THE GEOMETRY IS ON THE SERVER, and this guard is what
     makes the rest of this function a legacy path rather than the normal one. A
     device-local mesh exists for one reason: to feed a framed modeller that would
     otherwise rebuild the bridge from its parameters. A model whose `.glb` is in the
     bucket is not drawn by a modeller at all — src/modeler/meshView.ts renders the
     file — so there is nothing on this device for a replay to fix, and replaying one
     would spend a minute and a hidden modeller writing a cache nothing reads. */
  if (model.hasGeometry === true) {
    actions.clearModelMeshState();
    return;
  }

  /* A parametric model needs no mesh — it is rebuilt from its parameters wherever
     it is opened — so there is nothing to know and nothing to withhold the viewport
     for. `!== true` rather than `=== false`: null is the server saying it cannot
     tell, and a maybe is not a basis for replaying an import over a model. */
  if (model.imported !== true) {
    actions.clearModelMeshState();
    return;
  }
  if (store.get().modelImport) return;

  /* THE CHECK IS NOT GUARDED BY `restoreTried` — only the repair is. The viewport
     will not mount until this has answered (see the note on modelMeshState), so a
     record that returns to a model whose answer was cleared has to be able to ask
     again. Guarding both together left the viewport waiting for an answer nothing
     was going to produce a second time. */
  const known = store.get().modelMeshState;
  const present =
    known?.modelId === model.id ? known.present : await hasLocalMesh(model.id);
  actions.setModelMeshState(model.id, present);
  if (present) return;

  const seen = `${bridgeId}/${model.id}`;
  if (restoreTried.has(seen)) return;
  restoreTried.add(seen);

  /* NOTHING TO REPLAY, AND THE RECORD HAS TO SAY SO. A model imported before the
     export was kept has no source in the bucket, its mesh is on whichever machine
     did the importing, and no amount of waiting will bring it here. That is not a
     state to leave looking like a slow load: the panel would mount a viewport over
     an empty scene and the caption would name a model as though it were on screen.
     Flagged instead, and the record offers the one thing that fixes it. */
  if (!model.hasSource) {
    console.warn(
      `[restore] ${model.id} displays an imported mesh, this device has none, ` +
        'and no source export was stored — it has to be imported again.',
    );
    return;
  }

  /* Re-checked after the await. The mesh check is a database round trip, and an
     import started while it was in flight would make this the second writer of the
     same model's state — last write wins, silently. */
  if (store.get().modelImport) return;

  let texts: string[];
  try {
    texts = await api.fetchModelSource(bridgeId, model.id);
  } catch (err) {
    console.warn(`[restore] could not fetch the stored export for ${model.id}`, err);
    return;
  }
  if (texts.length === 0) return;

  console.info(`[restore] replaying the stored export for ${model.id} on this device`);
  actions.beginModelRestore(bridgeId, model);
  await runImport(bridgeId, model, texts, 'restore');

  /* ASKED AGAIN RATHER THAN ASSUMED. A restore that reported success still has to
     be checked: what it did was drive a modeller into writing a mesh, and
     MeshStore.SaveImportedMesh resolves false rather than throwing when it cannot
     (private mode, a blocked upgrade). Believing the run instead of the store is
     how the viewport would come back up over a bridge rebuilt from parameters —
     the exact failure this whole path exists to stop. */
  actions.setModelMeshState(model.id, await hasLocalMesh(model.id));
}

/**
 * Run one import from start to finish, and tell the store how it went.
 *
 * Reads as a sequence because it is one: boot, apply, save, tear down. Each
 * `await` is a specific message from the frame with a deadline on it, so a frame
 * that dies quietly — a chunk that never arrives, a WebGL context that could not
 * be created — ends as a reported failure rather than a spinner nobody can clear.
 */
async function runImport(
  bridgeId: string,
  model: { id: string; name: string },
  texts: string[],
  /**
   * `'import'` is a person choosing a file. `'restore'` is this device replaying
   * an export the server already has, because the mesh is not here — see
   * restoreImportedModel below.
   *
   * The frame work is identical, which is the point: a restore is not a second,
   * lesser code path that only runs when something has already gone wrong. What
   * differs is the two ends. A restore does not upload the source (it came FROM the
   * server) and does not re-export the geometry (an export of the same state would
   * produce the same bytes that are already in the bucket).
   */
  mode: 'import' | 'restore' = 'import',
): Promise<void> {
  /* THE EXPORT GOES TO THE SERVER FIRST, before a frame is opened and before
     anything is applied.

     An imported model's triangles are not in its state. The vendored
     io/MeshStore.ts keeps them in IndexedDB — a span is 236 KB, an 18-span flyover
     6.5 MB, against ~5 MB of localStorage for an entire origin — and the shim
     syncs localStorage only. So a model imported and saved with nothing else stored
     is a bridge that exists in ONE BROWSER, while its state tells every other
     browser `hasImportedMesh: true` and leaves them drawing nothing. IndexedDB is
     keyed by origin, so "another browser" includes this one on another port, which
     is how the hole was found: change the port, and the model is gone.

     FIRST, AND FATAL IF IT FAILS. Those two go together. This is the one step whose
     failure can be made harmless — nothing has been opened, nothing applied, and
     what is left behind is a model shell with no bridge in it, which the record
     already reports and the next import lands in. Run it last and a failure would
     leave precisely the broken state this exists to prevent, with the import
     reporting success. So it is not best-effort like the geometry below: an import
     that cannot put the model on the server has not imported the model. */
  /* AND BEFORE ANY OF IT, MAKE SURE THIS BROWSER CAN KEEP A MESH AT ALL.
     A browser left with an empty `bridge-bim-modeler` database — which an earlier
     version of ./meshPresence.ts did merely by looking — accepts an import, draws it
     correctly, and stores no triangles: the vendored MeshStore asks for version 1,
     finds version 1 with no object store, and every save fails resolving false. The
     import then reads as a success and the model comes back on the next visit as the
     parametric bridge its parameters describe. Repaired here because it cannot be
     repaired anywhere else: the check that repairs it runs only for a model the index
     calls imported, and the models this broke are precisely the ones it does not.

     Awaited but never trusted to have worked, and never fatal — an import whose mesh
     cannot be stored is still reported honestly by the record afterwards. */
  await repairMeshStore();

  /* Whether the SERVER already made the geometry every device draws, out of the
     export it was just handed. When it did, there is nothing for a modeller to
     export and the frame below is not opened for it — see the note at the geometry
     step. Left false for a restore, which uploads no source and so derives nothing:
     the .glb it needs is already in the bucket from the original import. */
  let serverDerivedGeometry = false;

  if (mode === 'import') {
    actions.modelImportPhase('storing');
    try {
      const stored = await api.putModelSource(bridgeId, model.id, texts);
      serverDerivedGeometry = stored.geometry !== null;
      /* The `.glb` under this model id was just replaced, so the device's copy is
         certainly stale — the same reason src/modeler/geometry.ts drops it after a
         re-export. */
      if (serverDerivedGeometry) await dropGeometry(bridgeId, model.id);
      console.info(
        `[import] source stored for ${model.id}: ${stored.bytes} bytes` +
          (stored.geometry
            ? ` — geometry derived from it: ${stored.geometry.parts} parts, ` +
              `${stored.geometry.triangles} triangles, ${stored.geometry.bytes} bytes`
            : ` — no geometry derived: ${stored.geometryReason ?? 'the server did not say why'}`),
      );
    } catch (err) {
      await actions.finishModelImport({
        bridgeId,
        modelId: model.id,
        modelName: model.name,
        ok: false,
        error:
          `The export could not be stored on the server, so nothing was changed. ` +
          `Without it the model would exist only in this browser. ` +
          (err instanceof Error ? err.message : 'The upload failed.'),
        name: '',
        applied: [],
        warnings: [],
        saveUnconfirmed: false,
        geometryError: null,
      });
      return;
    }
  }

  /* The run was announced by the click handler, before the file was read — this
     only moves it on to the first phase that needs a frame. */
  actions.modelImportPhase('opening');

  const frame = h('iframe', {
    src: importSrc(store.get(), bridgeId, model.id),
    title: 'Importing a bridge model',
    'aria-hidden': 'true',
    tabindex: '-1',
    // Same sandbox as the record's viewport: allow-same-origin is required for
    // the storage shim, so this is not a boundary against the vendored code —
    // what it buys is that nothing in there can navigate the register away. No
    // downloads and no modals, and here that is load-bearing rather than tidy:
    // the import runs silent (see the modeller's ImportCSiBridgeDocuments), and
    // a modal in a frame nobody can see is a run that never finishes.
    sandbox: 'allow-scripts allow-same-origin',
  });
  const stage = h('div', { class: 'mimp-stage', 'aria-hidden': 'true' }, frame);
  document.body.appendChild(stage);

  /* One listener for the run, filtered to THIS frame. The record's viewport and
     the full-screen overlay both listen for the same messages from their own
     frames; `event.source` is what keeps the three apart. */
  let waiting: { match: (m: FrameMessage) => boolean; settle: (m: FrameMessage | null) => void } | null = null;
  let frameError: string | null = null;
  /* Whether the imported STATE has been confirmed saved, as against the still.
     Only counted once the import has been applied — the modeller autosaves once
     on boot as well (that is deliberate upstream, so a model that is opened and
     merely looked at is not lost), and that save is of the state this import is
     about to replace. */
  let applied = false;
  let stateSaved = false;

  const onMessage = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin) return;
    if (event.source !== frame.contentWindow) return;
    const data = event.data as FrameMessage | null;
    if (data?.source !== 'bridge-modeler') return;

    // Kept whatever else is going on: a failed save posts this and nothing
    // else, so without it a run that could not reach Cloudflare would time out
    // saying nothing about why.
    if (data.type === 'error' && typeof data.message === 'string') frameError = data.message;
    if (applied && data.type === 'saved' && lines(data.keys).includes(STATE_KEY + model.id)) {
      stateSaved = true;
    }

    if (waiting?.match(data)) {
      const settle = waiting.settle;
      waiting = null;
      settle(data);
    }
  };
  window.addEventListener('message', onMessage);

  /** The next message matching `match`, or null if it does not come in time. */
  const next = (match: (m: FrameMessage) => boolean, ms: number): Promise<FrameMessage | null> =>
    new Promise((resolve) => {
      const timer = window.setTimeout(() => {
        if (waiting?.settle === settle) waiting = null;
        resolve(null);
      }, ms);
      const settle = (m: FrameMessage | null): void => {
        window.clearTimeout(timer);
        resolve(m);
      };
      waiting = { match, settle };
    });

  const result: ModelImportResult = {
    bridgeId,
    modelId: model.id,
    modelName: model.name,
    ok: false,
    name: '',
    applied: [],
    warnings: [],
    error: null,
    saveUnconfirmed: false,
    geometryError: null,
  };

  try {
    const ready = await next((m) => m.type === 'ready', BOOT_MS);
    if (!ready) {
      result.error =
        frameError ??
        'The modeller did not start, so nothing was imported. Check your connection and try again.';
      return;
    }

    actions.modelImportPhase('importing');
    frame.contentWindow?.postMessage(
      { source: 'bridge-monitor', type: 'import-csibridge', files: texts },
      window.location.origin,
    );

    const imported = await next((m) => m.type === 'imported', APPLY_MS);
    if (!imported) {
      result.error = frameError ?? 'The import did not finish, and nothing was changed.';
      return;
    }
    if (imported.ok !== true) {
      result.error = imported.error ?? 'The export could not be imported.';
      return;
    }

    result.ok = true;
    applied = true;
    result.name = typeof imported.name === 'string' ? imported.name : '';
    result.applied = lines(imported.applied);
    result.warnings = lines(imported.warnings);

    /* Now wait for it to reach Cloudflare. The state goes first (500 ms
       debounce, then the shim's flush) and the new still follows it (4 s), and
       it is the still the record shows — so the wait is for that one, which
       implies the other. A save that does not confirm in time is not a failure:
       the flush below forces the bytes out regardless, and the modeller writes
       through the same shim as any other edit. It means this side did not SEE
       it land, which the summary says rather than claiming otherwise. */
    actions.modelImportPhase('saving');
    const saved = await next(
      (m) => m.type === 'saved' && lines(m.keys).includes(THUMB_KEY + model.id),
      SAVE_MS,
    );
    /* The still not arriving is not itself a problem worth reporting — the
       capture is skipped rather than failed if the canvas is not in a state to
       be photographed, and the previous one stands. The state is what has to
       have landed, so a confirmed state save answers for the import even when
       the picture does not follow. */
    result.saveUnconfirmed = saved === null && !stateSaved;
  } finally {
    window.removeEventListener('message', onMessage);
    /* The shim sits on an edit for 1.5 s before flushing, and `pagehide` does
       not fire when a frame is removed by script — so without asking first, an
       import torn down inside that window would be lost. Same reason the
       record's own viewport asks; see src/modeler/viewport.ts. */
    try {
      frame.contentWindow?.postMessage(
        { source: 'bridge-monitor', type: 'flush' },
        window.location.origin,
      );
    } catch {
      /* frame already gone — nothing left to ask */
    }
    stage.remove();

    /* AND NOW STORE THE GEOMETRY, once this frame is gone.
       The mesh an import applies does not live in the model state: the vendored
       io/MeshStore.ts keeps it in IndexedDB, because one real bridge's triangles
       exceed the whole localStorage quota. The shim syncs localStorage, so the
       mesh stayed on this device — and `hasImportedMesh: true` in the state put
       every OTHER device into viewer mode with no mesh to draw, which is a bridge
       missing its abutments, its columns and everything else the source drew.

       The .glb is the register's own device-independent copy of the rendered
       scene, and it already carries imported parts: `Canvas3D.Add` puts them
       under `canvas3d.root`, which is what `ExportGeometryToGlb` walks. Exporting
       it here means the geometry is in R2 by the time the summary appears.

       AFTER `stage.remove()`, deliberately. This boots a second frame on the same
       model, and two framed modellers on one model are two writers of its state —
       the thing the note at the top of this file exists to prevent.

       A FAILURE HERE IS NOT AN IMPORT FAILURE. The model is saved either way;
       what is lost is the register's own 3D view of it, which the record already
       reports through `geometryState` and offers a re-export for. So it is logged
       and carried in the summary rather than turning a good import into a bad
       one.

       AND IT IS NOT RUN AT ALL FOR AN EXPORT THAT CARRIED ITS OWN MESH, which is
       every CSiBridge cloud model. The server derived the .glb from the document on
       the way in (functions/lib/meshGlb.ts), so this would boot a second modeller to
       produce a second answer to a question already answered — and asking a modeller
       is the weaker way to ask. It walks whatever that frame's scene happens to
       contain, which is why the wrong bridge got stored in the first place: on a
       device whose mesh store was unusable the frame had rebuilt the model from its
       parameters, and `ExportGeometryToGlb` faithfully exported the rebuild. The
       derivation cannot make that mistake, because it has no parameters to build
       from. So the frame is opened only when nothing was derived — an export with no
       mesh in it, where a parametric render really is the only picture there is. */
    if (result.ok && mode === 'import' && !serverDerivedGeometry) {
      actions.modelImportPhase('geometry');
      try {
        const bytes = await exportModelGeometry(bridgeId, result.modelId);
        console.info(`[import] geometry stored for ${result.modelId}: ${bytes} bytes`);
      } catch (err) {
        result.geometryError =
          err instanceof Error ? err.message : 'The geometry could not be stored.';
        console.warn('[import] geometry export failed', err);
      }
    }

    await actions.finishModelImport(result);
  }
}

/* Nothing is exported but the entry point at the top. The frame, its message
   plumbing and the deadlines are this file's business — the rest of the app
   knows an import only as `modelImport` and `modelImportResult` in the store,
   which is what the record and the summary dialog read. */
